import * as React from 'react';
import { Slider as SliderPrimitive } from 'radix-ui';

import { cn } from '@/lib/utils';

const THUMB_PX = 16;
const FINE_STEPS = 100;
// Radix moves the thumb's unstyled wrapper span, which only a selector reaches.
const THUMB_GLIDE =
  'motion-safe:[&>span:has(>[role=slider])]:transition-[left] motion-safe:[&>span:has(>[role=slider])]:duration-200 motion-safe:[&>span:has(>[role=slider])]:ease-out';

type SliderProps = React.ComponentPropsWithoutRef<
  typeof SliderPrimitive.Root
> & {
  /** A dot at every step, for a slider over a few fixed choices. */
  showSteps?: boolean;
  /** Drag freely between steps, then glide to the nearest one on release. */
  smooth?: boolean;
};

const Slider = React.forwardRef<
  React.ElementRef<typeof SliderPrimitive.Root>,
  SliderProps
>(
  (
    {
      className,
      showSteps,
      smooth,
      min = 0,
      max = 100,
      step = 1,
      value,
      defaultValue,
      onValueChange,
      onValueCommit,
      onKeyDown,
      onLostPointerCapture,
      'aria-label': ariaLabel,
      'aria-valuetext': ariaValueText,
      ...props
    },
    ref
  ) => {
    const [uncontrolled, setUncontrolled] = React.useState(
      defaultValue?.[0] ?? min
    );
    const [dragAt, setDragAt] = React.useState<number | null>(null);
    const settled = value?.[0] ?? uncontrolled;
    const position = smooth ? (dragAt ?? settled) : settled;
    const gliding = smooth && dragAt === null;

    const nearest = (at: number) =>
      Math.min(max, Math.max(min, min + Math.round((at - min) / step) * step));
    const choose = (next: number) => {
      setUncontrolled(next);
      onValueChange?.([next]);
    };
    const settle = (at: number) => {
      setDragAt(null);
      choose(nearest(at));
      onValueCommit?.([nearest(at)]);
    };
    const stepKeys: Record<string, (at: number) => number> = {
      ArrowRight: (at) => at + step,
      ArrowUp: (at) => at + step,
      PageUp: (at) => at + step,
      ArrowLeft: (at) => at - step,
      ArrowDown: (at) => at - step,
      PageDown: (at) => at - step,
      Home: () => min,
      End: () => max,
    };

    const smoothProps: Partial<
      React.ComponentPropsWithoutRef<typeof SliderPrimitive.Root>
    > = smooth
      ? {
          step: step / FINE_STEPS,
          value: [position],
          onValueChange: ([at]) => {
            setDragAt(at);
            choose(nearest(at));
          },
          onValueCommit: ([at]) => settle(at),
          // Radix skips its own key handling once this prevents the default.
          onKeyDown: (event) => {
            onKeyDown?.(event);
            const move = stepKeys[event.key];
            if (event.defaultPrevented || !move) return;
            event.preventDefault();
            settle(move(settled));
          },
          // A drag that ends where it began commits nothing, so settle here.
          onLostPointerCapture: (event) => {
            onLostPointerCapture?.(event);
            if (dragAt !== null) settle(dragAt);
          },
        }
      : {
          step,
          value,
          defaultValue,
          onValueChange: (next) => {
            setUncontrolled(next[0]);
            onValueChange?.(next);
          },
          onValueCommit,
          onKeyDown,
          onLostPointerCapture,
        };

    const steps =
      showSteps && max > min ? Math.floor((max - min) / step) + 1 : 0;
    return (
      <SliderPrimitive.Root
        ref={ref}
        min={min}
        max={max}
        className={cn(
          'relative flex h-5 w-full touch-none items-center select-none data-disabled:cursor-not-allowed data-disabled:opacity-50',
          gliding && THUMB_GLIDE,
          className
        )}
        {...props}
        {...smoothProps}
      >
        <SliderPrimitive.Track className='relative h-1.5 w-full grow overflow-hidden rounded-full bg-muted'>
          <SliderPrimitive.Range
            className={cn(
              'absolute h-full bg-primary',
              gliding &&
                'motion-safe:transition-[left,right] motion-safe:duration-200 motion-safe:ease-out'
            )}
          />
        </SliderPrimitive.Track>
        {Array.from({ length: steps }, (_, index) => {
          const at = min + index * step;
          const percent = ((at - min) / (max - min)) * 100;
          return (
            <span
              key={at}
              aria-hidden
              className={cn(
                'pointer-events-none absolute top-1/2 size-1 -translate-x-1/2 -translate-y-1/2 rounded-full motion-safe:transition-colors motion-safe:duration-200',
                at <= position
                  ? 'bg-primary-foreground/70'
                  : 'bg-muted-foreground/40'
              )}
              // Matches Radix, which keeps the thumb inside the track.
              style={{
                left: `calc(${percent}% + ${THUMB_PX / 2 - (percent / 100) * THUMB_PX}px)`,
              }}
            />
          );
        })}
        <SliderPrimitive.Thumb
          aria-label={ariaLabel}
          aria-valuetext={ariaValueText}
          data-dragging={dragAt !== null || undefined}
          className='block size-4 cursor-grab rounded-full border-2 border-primary bg-background shadow-sm hover:scale-110 focus-visible:ring-4 focus-visible:ring-ring/30 focus-visible:outline-hidden data-disabled:pointer-events-none data-dragging:scale-125 data-dragging:cursor-grabbing data-dragging:ring-4 data-dragging:ring-primary/20 motion-safe:transition-[scale,box-shadow] motion-safe:duration-150'
        />
      </SliderPrimitive.Root>
    );
  }
);
Slider.displayName = SliderPrimitive.Root.displayName;

export { Slider };
